import { clampCandidatesPerGeneration } from './comicProjectSettings'
import type { ComicPage, ComicPageImageStatus } from './comicTypes'

/** 分页芯片内「整页定稿」状态点：选中芯片为 accent 底时需浅色点，否则主题色点会看不见 */
export function pageImageCommitDotClass(
  committed: boolean,
  onAccentBg: boolean,
  angular: boolean,
): string {
  const shape = angular ? 'rounded-none ring-2' : 'rounded-full ring-1'
  if (onAccentBg) {
    return committed
      ? ['bg-white ring-white/50', shape].join(' ')
      : ['bg-white/35 ring-white/25', shape].join(' ')
  }
  return committed
    ? ['bg-accent ring-[color-mix(in_srgb,var(--accent)_38%,transparent)]', shape].join(' ')
    : [
        'bg-[color-mix(in_srgb,var(--ink)_14%,var(--line)_58%)]',
        'ring-[color-mix(in_srgb,var(--line)_62%,transparent)]',
        shape,
      ].join(' ')
}

/** 画布上是否已有可展示的整页图（已定稿 URL 或候选图） */
export function pageHasWholePagePreview(page: ComicPage): boolean {
  if (page.pageImageUrl?.trim()) return true
  return page.pageCandidates.some((c) => c.imageUrl?.trim())
}

/** 是否显示「生成中」遮罩（有成图预览时不再盖住画面） */
export function pageShowsGeneratingOnCanvas(page: ComicPage): boolean {
  return page.pageImageStatus === 'generating' && !pageHasWholePagePreview(page)
}

/** 本页是否已有应用的整页成图（用于分页条左侧状态点） */
export function pageHasAppliedPageImage(page: ComicPage): boolean {
  if (page.pageImageUrl?.trim()) return true
  if (!page.selectedPageCandidateId) return false
  return page.pageCandidates.some((c) => c.id === page.selectedPageCandidateId)
}

export function comicPageImageStatusLabel(status: ComicPageImageStatus | undefined): string {
  switch (status) {
    case 'generating':
      return '生成中'
    case 'pending_selection':
      return '待选定'
    case 'done':
      return '已定稿'
    case 'failed':
      return '失败'
    default:
      return '草稿'
  }
}

/** 与 ChromeMetaTag 搭配的语义色（沿用主题变量，避免硬编码主题名） */
export function comicPageImageStatusClass(status: ComicPageImageStatus | undefined): string {
  switch (status) {
    case 'generating':
      return 'border-[color-mix(in_srgb,var(--line)_62%,rgb(234_179_89)_38%)] bg-[color-mix(in_srgb,var(--panel)_90%,rgb(254_239_218)_10%)]'
    case 'pending_selection':
      return 'border-[color-mix(in_srgb,var(--line)_55%,var(--accent)_45%)] bg-[color-mix(in_srgb,var(--panel)_88%,var(--accent)_12%)]'
    case 'done':
      return 'border-[color-mix(in_srgb,var(--line)_52%,var(--accent)_48%)] bg-[color-mix(in_srgb,var(--panel)_90%,var(--accent)_10%)]'
    case 'failed':
      return 'border-[color-mix(in_srgb,var(--line)_58%,rgb(236_148_157)_42%)] bg-[color-mix(in_srgb,var(--panel)_90%,rgb(251_218_223)_10%)]'
    default:
      return 'border-[color-mix(in_srgb,var(--line)_70%,var(--ink-soft)_30%)] bg-[color-mix(in_srgb,var(--panel)_92%,var(--ink-soft)_8%)]'
  }
}

export function shouldRequirePageSelection(candidatesPerGeneration: unknown): boolean {
  return clampCandidatesPerGeneration(candidatesPerGeneration) > 1
}

export function pageShowsCandidatePicker(
  status: ComicPageImageStatus | undefined,
  candidateCount: number,
  hasAppliedImage: boolean,
): boolean {
  if (candidateCount === 0) return false
  if (status === 'pending_selection') return true
  return candidateCount > 1 && !hasAppliedImage
}
